/**
 * Chatters — vier tabbladen, precies zoals de huidige pagina:
 *   Performance · Shift agenda · Roster & settings · Compensation & activity
 *
 * Endpoints:
 *   /api/admin/chatter-finance/performance-summary   (alles voor Performance)
 *   /api/admin/chatter-finance/shift-alerts          (Shift agenda)
 *   /api/admin/models/chatters                       (Roster & settings)
 *   /api/admin/chatter-finance/compensation-roster   (Compensation)
 *   /api/admin/dashboard/takeovers-by-chatter
 *
 * Harde eis van Jordy: ALLE per-chatter velden en ALLE toegangsrechten
 * zichtbaar — welke pagina's en opties per chatter aan of uit staan. Die
 * staan in het tabblad "Roster & settings" als volledige rechtenmatrix.
 */
import { useEffect, useMemo, useState } from 'react'
import { Empty, ErrorNote, Kpi, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { MassDmPresetDialog } from '../components/MassDmPresetDialog'
import { ChatterEditDialog, utcLabel } from '../components/ChatterEditDialog'
import { ChatterAccountDialog } from '../components/ChatterAccountDialog'
import { ModelNaam } from '../components/ModelNaam'
import { VerwijderDialog } from '../components/VerwijderDialog'
import type { ModelOptie } from '../components/ModelKeuze'
import { isBeheer, isVolledigBeheer, magAccountsAanmaken, magTeamPerformance, useMij } from '../lib/rechten'
import type { ChatterRights } from '../components/ChatterEditDialog'
import { useActions, mut } from '../lib/actions'
import {
  BarChartPanel, DayTiles, DonutChartPanel, FunnelChartPanel, HOOGTE, KLEUREN,
  LineChartPanel,
} from '../components/charts'
import { useApi, usePoll } from '../lib/useApi'
import { ago, dt, eur, num, pct } from '../lib/format'
import { useNavigate } from 'react-router-dom'
import { Modal } from '../components/Modal'
import { BlokkeerDialoog, ShiftAgenda } from '../components/ShiftAgenda'
import type { BlokDoel } from '../components/ShiftAgenda'

interface ChatterRow {
  chatter_id: number; name: string; is_online: boolean; active: boolean
  online_minutes: number; online_hours: number
  messages_sent: number
  regular_messages: number; mass_messages: number; mass_campaigns: number
  mass_followers_reached: number
  sales: number; gross: number; net: number; conversion_pct: number
  avg_response_seconds: number; last_seen_at: string | null; last_message_at: string | null
  efficiency_score: number; net_per_hour: number; messages_per_hour: number
  gross_to_net_pct: number; share_of_attributed_net_pct: number
  own_commission: number; referral_earned: number; total_earned: number
  open_commission: number; paid_commission: number
  commission_pct: number | null; commission_base: string | null
  is_lead: boolean; lead_chatter_name: string | null; status: string
  lead_override_pct: number | null
  /** De modellen waar deze chatter aan gekoppeld is. */
  assigned_models: { model_persona_id: number; model_name: string
                     model_slug: string }[] | null
  /** Omzet per platform; de server stuurt een LIJST (geen object). */
  platform_breakdown: { platform: string; gross: number; net: number
                        sales: number; share_pct: number }[] | null
  /** Omzet per model: naam, netto en aandeel. */
  model_breakdown: { model_persona_id: number; model_name: string
                     gross: number; net: number; sales: number
                     share_pct: number }[] | null
  daily_activity: {
    date: string; minutes: number
    /** De losse sessies van die dag; oud klapt ze uit onder de datum. */
    sessions?: { clock_in: string; clock_out: string | null
                 minutes: number; last_page?: string | null }[]
  }[] | null
}
interface Summary {
  totals: Record<string, number>
  alerts: { id: string; tone: string; title: string; detail: string; timestamp: string }[]
  alert_rules: Record<string, number>
  activity_overview: {
    total_online_minutes: number; active_days: number
    average_session_minutes: number; longest_session_minutes: number
    shortest_session_minutes: number
    average_online_minutes_per_day: number
    heatmap: { date: string; minutes: number }[]
  }
  charts: Record<string, Record<string, string | number>[]>
  funnel: { label: string; count: number; pct: number }[]
  chatters: ChatterRow[]
  unlocks_and_tips: VerkoopRuw[]
  content_sends: ContentSend[]
  latest_transactions: VerkoopRuw[]
  live_activity: LiveRow[]
  /** De verdeling van de omzet over platform en model. */
  platform_mix: { platform: string; gross: number; net: number
                  sales: number; share_pct: number }[]
  model_mix: { model_persona_id: number; model_name: string; gross: number
               net: number; sales: number; share_pct: number }[]
}
interface UnlockRow {
  /** De revenue_event-id; koppelt aan /chatter-finance/sale-attribution. */
  id?: number
  timestamp: string; platform: string; model: string; fan: string
  type: string; chatter: string | null; attribution: string | null
  gross: number; net: number
  /** 2-11-2026 (Jordy): bepaalt of de knop "Assign" of "Change" heet. */
  chatter_id?: number | null
}

/**
 * Een verkooprij zoals de server hem echt stuurt.
 *
 * 28-10-2026 (Jordy): bij Unlocks & Tips en Latest chatter sales waren Time,
 * Model, Type en Chatter leeg. De pagina las `timestamp`, `model`, `type` en
 * `chatter`, maar performance-summary stuurt `created_at`, `model_name`,
 * `label`/`event_type` en `chatter_name`. Hier op één plek vertaald; de oude
 * namen blijven werken als de server ze ooit wel stuurt.
 */
interface VerkoopRuw {
  id?: number
  created_at?: string | null; timestamp?: string | null
  platform?: string | null
  model_name?: string | null; model?: string | null
  fan?: string | null
  label?: string | null; event_type?: string | null; type?: string | null
  chatter_name?: string | null; chatter?: string | null
  chatter_id?: number | null
  attribution_reason_label?: string | null; attribution_method?: string | null
  attribution?: string | null
  gross?: number | null; net?: number | null
}
function verkoop(u: VerkoopRuw): UnlockRow {
  return {
    id: u.id,
    timestamp: String(u.timestamp ?? u.created_at ?? ''),
    platform: String(u.platform ?? ''),
    model: String(u.model ?? u.model_name ?? ''),
    fan: String(u.fan ?? ''),
    type: String(u.type ?? u.label ?? u.event_type ?? ''),
    chatter: u.chatter ?? u.chatter_name ?? null,
    chatter_id: u.chatter_id ?? null,
    // Zonder chatter zegt de reden WAAROM er niemand aan hangt.
    attribution: u.attribution ?? u.attribution_reason_label ?? u.attribution_method ?? null,
    gross: Number(u.gross ?? 0),
    net: Number(u.net ?? 0),
  }
}

/** Wie een verkoop maakte, los van wie het gesprek toegewezen kreeg (A1).
 *  Rapportage: de uitbetaling blijft op de toegewezen chatter gebaseerd. */
interface Toewijzing {
  revenue_event_id: number
  sold_by_chatter_name: string | null
  attribution_confidence: 'high' | 'medium' | 'low' | 'none' | null
  attribution_method: string | null
}
interface ContentSend {
  timestamp: string; chatter: string | null; platform: string; model: string
  fan: string; preview: string | null; is_paid: boolean; price: number | null
  delivered: boolean; delivery_error: string | null
  sale_gross: number | null; sale_net: number | null
}
interface LiveRow {
  timestamp: string; type: string; chatter: string | null; platform: string
  model: string; fan: string; title: string | null; detail: string | null
  is_paid: boolean; price: number | null
  /** 'fan' of de kant van de chatter — bepaalt hoe de regel wordt geschreven. */
  sender_kind: string | null
  sender_admin_name: string | null
  has_media: boolean | null
  media_type: string | null
  /** Het begin van het bericht; oud zet dat onder de regel. */
  preview: string | null
}
interface MassMsg {
  id: number; created_at: string; created_by: string | null; platform: string
  model_name: string; content: string; kind: string; is_paid: boolean
  recipient_count: number; sent_count: number; failed_count: number; status: string
  /** Wat de server over de aflevering weet (_bezorging_velden). */
  delivered?: number | null
  delivered_source?: string | null
  recipients_estimated?: boolean
  copies_in_chats?: number | null
}

/**
 * Een platformcampagne naar alle volgers ("native"): het platform verstuurt
 * zelf; wij krijgen alleen "geaccepteerd" terug en geen aantallen.
 */
function isNativeCampagne(m: MassMsg): boolean {
  const k = String(m.kind ?? '')
  return k.includes('native_all_followers')
    || (String(m.platform ?? '').toLowerCase() === 'fancentro' && k === 'media_all_followers')
}

/**
 * Het publiek in gewone woorden. 28-10-2026 (Jordy): de kolom toonde de
 * interne soort, bijvoorbeeld "text_native_all_followers".
 */
function publiekTekst(m: MassMsg): string {
  const k = String(m.kind ?? '').toLowerCase()
  const soort = k.startsWith('media') ? 'Media' : k.startsWith('text') ? 'Text' : ''
  let wie: string
  if (k.includes('all_followers')) wie = 'All followers'
  else if (k.includes('spender')) wie = 'Spenders'
  else if (k.includes('subscriber')) wie = 'Subscribers'
  else if (Number(m.recipient_count ?? 0) > 0) wie = `${groep(m.recipient_count)} fans`
  else wie = 'Selected fans'
  return [soort, wie].filter(Boolean).join(' · ')
}

/** Het label voor "Sent": bij een native campagne geen schijnbare "0 / N". */
function verzondenTekst(m: MassMsg): string {
  if (isNativeCampagne(m) && !Number(m.sent_count ?? 0)) {
    return String(m.status ?? '').toLowerCase() === 'failed'
      ? '—' : 'Accepted by platform, delivery not confirmed'
  }
  return `${groep(m.sent_count)} / ${groep(m.recipient_count)}`
}

/** Deels of helemaal mislukt: dan hoort de route naar "Resend remaining" erbij. */
function moetNaversturen(m: MassMsg): boolean {
  const verstuurd = Number(m.sent_count ?? 0)
  const ontvangers = Number(m.recipient_count ?? 0)
  const mislukt = Number(m.failed_count ?? 0)
  if (isNativeCampagne(m)) return String(m.status ?? '').toLowerCase() === 'failed'
  return String(m.status ?? '').toLowerCase() === 'failed' || mislukt > 0
    || (ontvangers > 0 && verstuurd < ontvangers
        && ['done', 'cancelled', 'interrupted'].includes(String(m.status ?? '').toLowerCase()))
}

/**
 * Testaccounts herkennen. Er is geen vlag in de database (gecontroleerd
 * 28-10-2026); daarom op naam, en bewust streng: "test", "test1234", "demo",
 * of een los woord test/demo/qa in de naam.
 */
function isTestAccount(naam: unknown): boolean {
  const n = String(naam ?? '').trim()
  return /^(test|demo)\d*$/i.test(n) || /(^|[\s_-])(test|demo|qa)($|[\s_-])/i.test(n)
}
interface Chatter {
  id: number; name: string; active: boolean; account_active: boolean
  has_login: boolean; is_lead: boolean; is_monitor_only: boolean
  lead_chatter_id: number | null
  /** 2-11-2026: de login achter de kaart en of hij direct onder mij valt. */
  admin_user_id?: number | null
  is_direct_report?: boolean
  telegram_chat_id: string | null
  telegram_user_id?: string | null
  mass_dm_filter_grants?: string[]
  assigned_models: {
    model_persona_id: number; model_name: string
    model_slug?: string | null; is_primary?: boolean
    assignment_id?: number
  }[]
  allow_chat_blocking: boolean; allow_chatter_dashboard: boolean
  allow_create_chatter_accounts: boolean; allow_followers: boolean
  allow_mass_dm: boolean; allow_native_mass_dm: boolean
  allow_paid_mass_dm: boolean; allow_private_manage_all: boolean
  allow_private_self_claim: boolean; allow_timewaster_mark: boolean; allow_vip_mark: boolean
  allow_toggle_ai: boolean; hide_earnings: boolean; view_all_unlocks: boolean
  lead_override_pct: number | null; last_active_at: string | null
  /** 24-10-2026: sinds wanneer de koppeling telt (UTC, Z); alleen voor de owner. */
  referral_since?: string | null
}

/* De bundle rendert dit als MUI-Tabs met deze vijf waarden en labels:
   performance · shifts · team · roster · compensation. "Mass DM filter
   preset" is géén tabblad maar een dialoog naast de tabbalk. */
const TABS = [
  { value: 'perf', label: 'Performance' },
  { value: 'shift', label: 'Shift agenda' },
  // Oud kent wel een tabblad "Team setup", maar toont het achter een
  // rechtencontrole: in de bundle staat het als `s && <Tab value="team">`, en
  // op het echte scherm van dit account staat het niet in de DOM. Daarom hier
  // ook niet. De terugkerende shift-series die eronder hingen staan nu bij de
  // agenda, waar ze thuishoren.
  { value: 'roster', label: 'Roster & settings' },
  // 28-10-2026 (Jordy): "Compensation & activity" staat nu in Settings, alleen
  // voor owner/admin. Hier geen tabblad, geen route en geen aanvraag meer.
]
/** De compensatiekolommen van "Detailed chatter performance": alleen
 *  owner/admin (28-10-2026, Jordy). Verkopen (gross, net, mix) ziet iedereen
 *  die deze pagina mag openen; de server stuurt de rest niet mee. */
const BELONINGKOLOMMEN_DETAIL = ['cut', 'ref', 'due', 'op']

const RANGES = [
  { value: '1', label: 'Today' }, { value: '7', label: 'Week' },
  { value: '30', label: 'Month' }, { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' },
]
const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

/** Alle toegangsrechten die per chatter aan/uit kunnen — de harde eis. */

/** "75h 44m" — oud schrijft uren met een h, niet met een u. */
/**
 * Een sparkline uit een reeks datapunten: alleen de waarden, de kaart maakt
 * er een lijntje van. Oud toont die lijn ook; zonder verloop zegt een
 * kengetal weinig over de richting.
 */
function vonk(reeks: unknown, veld: string): number[] | undefined {
  if (!Array.isArray(reeks) || reeks.length < 2) return undefined
  return reeks.map((p) => Number((p as Record<string, unknown>)[veld] ?? 0))
}

/** "€754,99 / €250,00 · 302%" — hoever we zijn tegenover het doel. */
function doel(
  waarde: unknown, norm: unknown, opmaak: (v: number) => string,
): { text: string; gehaald: boolean } | undefined {
  const n = Number(norm ?? 0)
  if (!n) return undefined
  const w = Number(waarde ?? 0)
  return {
    text: `${opmaak(w)} / ${opmaak(n)} · ${Math.round((w / n) * 100)}%`,
    gehaald: w >= n,
  }
}

/** Bij een reactietijd is LAGER beter; dat draait het oordeel om. */
function onderDoel(waarde: unknown, norm: unknown):
    { text: string; dir: 'up' | 'down' } | undefined {
  const n = Number(norm ?? 0)
  if (!n) return undefined
  const w = Number(waarde ?? 0)
  return { text: w <= n ? 'on target' : 'above target', dir: w <= n ? 'up' : 'down' }
}

/** "+12.4% vs previous" — de verandering tegenover het vorige venster. */
function trend(pctWaarde: unknown): { text: string; dir: 'up' | 'down' } | undefined {
  const n = Number(pctWaarde ?? 0)
  if (!Number.isFinite(n) || n === 0) return undefined
  return {
    text: `${n > 0 ? '+' : ''}${n.toFixed(1)}% vs previous`,
    dir: n > 0 ? 'up' : 'down',
  }
}


/**
 * De soort van een alarm, met de badge die oud eraan hangt.
 *
 * Oud onderscheidt vijf: Warn en High voor wat misgaat, Pay voor uitbetaling,
 * Note voor een aantekening en Info voor de rest. De toon komt van de server;
 * het soort alarm bepaalt welk woord erbij hoort.
 */
function alarmSoort(a: { tone?: string | null; kind?: string | null }):
    { label: string; kind: 'bad' | 'warn' | 'ok' | 'acc' | 'mut' } {
  const kind = String(a.kind ?? '').toLowerCase()
  const tone = String(a.tone ?? '').toLowerCase()
  if (kind.includes('payout') || kind.includes('payment')) {
    return { label: 'Pay', kind: 'ok' }
  }
  if (kind.includes('note') || kind.includes('notice')) return { label: 'Note', kind: 'acc' }
  if (tone === 'danger' || tone === 'bad' || tone === 'error') {
    return { label: 'High', kind: 'bad' }
  }
  if (tone === 'warning' || tone === 'warn') return { label: 'Warn', kind: 'warn' }
  return { label: 'Info', kind: 'mut' }
}


/** De labels onder een grafiek: "00:00" bij uren, "06-09" bij dagen. */
function asLabels(reeks: Record<string, unknown>[], opUur: boolean): string[] {
  return reeks.map((p) => {
    if (opUur) return `${String(p.hour ?? '').padStart(2, '0')}:00`
    const d = String(p.date ?? '')
    // "2026-09-06" wordt "06-09": dag eerst, zoals oud het schrijft.
    return d.length >= 10 ? `${d.slice(8, 10)}-${d.slice(5, 7)}` : d
  })
}

/** Eén veld uit een reeks datapunten als kale getallenrij. */
function waardenVan(reeks: Record<string, unknown>[], veld: string): number[] {
  return reeks.map((p) => Number(p[veld] ?? 0))
}


/**
 * De statusbadge van een chatter, zoals oud hem bepaalt.
 *
 * Oud kent vier uitkomsten, nieuw maar twee: wie even weg was kreeg hier
 * "Online" terwijl oud "Away" toont, en een chatter die op toewijzing wacht
 * werd niet als zodanig herkend. Het veld `status` van de server is leidend;
 * ontbreekt het, dan valt oud terug op de online-vlag.
 */
function statusBadge(status: unknown, isOnline: boolean):
    { label: string; kind: 'ok' | 'warn' | 'acc' | 'mut' } {
  const s = String(status ?? (isOnline ? 'online' : 'offline')).toLowerCase()
  if (s === 'online') return { label: 'Online', kind: 'ok' }
  if (s === 'away') return { label: 'Away', kind: 'warn' }
  if (s === 'review') return { label: 'Needs assignment', kind: 'acc' }
  return { label: 'Offline', kind: 'mut' }
}

/** "75h 44m" — oud schrijft uren met een h, niet met een u. */
function mins(v: unknown): string {
  const n = Number(v ?? 0)
  if (!n) return '0m'
  if (n < 60) return `${Math.round(n)}m`
  return `${Math.floor(n / 60)}h ${Math.round(n % 60)}m`
}

/**
 * Een aantal met duizendtalscheiding, zoals het Leaderboard het toont:
 * "1.439" en niet "1439".
 *
 * Dat wijkt af van de rest van de pagina, waar oud getallen ongegroepeerd
 * laat staan. De opmaak volgt oud per blok, niet per pagina.
 */
function groep(v: unknown): string {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n.toLocaleString('nl-NL') : '0'
}
function secs(v: unknown): string {
  const n = Number(v ?? 0)
  if (!n) return '—'
  return n < 60 ? `${n.toFixed(0)}s` : `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`
}

/**
 * Eén regel in de Live activity, in de bewoordingen van het oude scherm.
 *
 * Een bericht van de fan leest als "eye2004 messaged fc_ivyskyexx"; een
 * antwoord van ons als "Max replied to eye2004 in fc_ivyskyexx", en met een
 * bijlage als "Max sent paid photo to eye2004 in fc_ivyskyexx". Is niet te
 * achterhalen wie er antwoordde, dan schrijft oud "fc • chatter unknown" —
 * dat is nuttige informatie, geen fout.
 */
function activiteitRegel(a: LiveRow): string {
  const model = a.model ? ` in ${a.model}` : ''
  if (String(a.sender_kind ?? '').toLowerCase() === 'fan') {
    return `${a.fan || 'A fan'} messaged${a.model ? ` ${a.model}` : ''}`
  }
  const wie = a.sender_admin_name || a.chatter
    || `${String(a.platform ?? '').toLowerCase()} • chatter unknown`
  if (a.has_media) {
    const soort = a.is_paid ? 'paid' : 'free'
    return `${wie} sent ${soort} ${a.media_type || 'content'} to ${a.fan || 'a fan'}${model}`
  }
  return `${wie} replied to ${a.fan || 'a fan'}${model}`
}

/**
 * Eén vakje in de activiteitenmatrix: een balk waarvan de lengte én de
 * kleurintensiteit meebewegen met de waarde, met het echte getal ernaast.
 * Zonder dat getal is een matrix mooi maar onbruikbaar.
 */
function MatrixCel({ waarde, piek, kleur, tekst }: {
  waarde: number; piek: number; kleur: string; tekst: string
}) {
  const deel = piek > 0 ? Math.max(0, Math.min(1, waarde / piek)) : 0
  return (
    <span className="cel">
      <span className="balk">
        <span
          className="vul"
          style={{
            width: `${Math.max(2, deel * 100)}%`,
            background: kleur,
            opacity: 0.25 + deel * 0.65,
          }}
        />
      </span>
      <span className="getal">{tekst}</span>
    </span>
  )
}


/** "08:43" — alleen de klok uit een tijdstempel. */
function klok(v: unknown): string {
  if (!v) return '—'
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}`
}

/** "28 models · 29 platform profiles" — waar deze chatter op werkt. */
function modellenRegel(c: { assigned_models?: { model_persona_id: number }[] | null }): string {
  const toegewezen = c.assigned_models ?? []
  const modellen = new Set(toegewezen.map((m) => m.model_persona_id)).size
  return `${modellen} model${modellen === 1 ? '' : 's'} · `
    + `${toegewezen.length} platform profile${toegewezen.length === 1 ? '' : 's'}`
}

/**
 * De drempels achter het tandwiel bij de alarmen.
 *
 * Ze staan hier met een uitleg erbij, want een kaal veld als
 * "no_revenue_after_minutes" zegt niemand iets. De server valideert dezelfde
 * grenzen nog een keer; dit is alleen om te voorkomen dat iemand per ongeluk
 * een onmogelijke waarde intikt.
 */
/* 2-11-2026 (Jordy): de overige sleutels van /alert-rules erbij, met de
   grenzen uit save_chatter_alert_rules (admin_chatter_finance.py). `options`
   maakt er een keuzelijst van: de index is de waarde (0 = maandag, zoals
   payout_weekdays op de server). attribution_min_pct is het aandeel verkopen
   met een chatter (doel op de kaart "Conversion"); "Conversion target" is
   conversion_target_pct. */
const DREMPELS: {
  key: string; label: string; hint: string; step?: number
  min?: number; max?: number; options?: string[]
}[] = [
  { key: 'attribution_min_pct', label: 'Min. attributed sales (%)',
    hint: 'Alert when fewer sales are attributed to a chatter than this. '
      + 'Also the target on the Conversion card.' },
  { key: 'conversion_target_pct', label: 'Conversion target (%)', min: 0, max: 100,
    hint: 'Target share of new chats that turn into a sale.' },
  { key: 'response_max_seconds', label: 'Max response time (s)',
    hint: 'Alert when the average response time goes above this.' },
  { key: 'messages_per_hour_min', label: 'Min messages per hour',
    hint: 'Below this pace a chatter counts as too slow.', step: 0.5 },
  { key: 'no_revenue_after_minutes', label: 'No revenue after (min)',
    hint: 'Alert after this long online without revenue.' },
  { key: 'revenue_net_target', label: 'Net revenue target (EUR)',
    hint: 'The target on the Revenue net card.', step: 10 },
  { key: 'net_per_hour_target', label: 'Net per online hour (EUR)',
    hint: 'The target on the Net / online hour card.', step: 1 },
  { key: 'efficiency_target', label: 'Efficiency target',
    hint: 'The efficiency score target, from 0 to 100.' },
  { key: 'online_minutes_target', label: 'Online target (min)',
    hint: 'How many minutes per day should be worked online.', step: 30 },
  { key: 'shift_coverage_warn_pct', label: 'Shift coverage review under (%)', min: 1, max: 100,
    hint: 'Review chip on the shift when online coverage is below this (1–100).' },
  { key: 'shift_offline_realert_minutes', label: 'Offline re-alert every (min)', min: 5, max: 180,
    hint: 'Telegram re-alert while a chatter stays offline/away during a shift (5–180).' },
  { key: 'shift_midshift_check_fraction', label: 'Mid-shift check at (fraction)',
    min: 0.1, max: 0.9, step: 0.05,
    hint: 'When to check coverage once, as a fraction of the shift: 0.5 = halfway (0.1–0.9).' },
  { key: 'shift_midshift_coverage_warn_pct', label: 'Mid-shift alert under (%)', min: 0, max: 100,
    hint: 'One-shot Telegram alert at the mid-shift check if coverage is below this (0–100).' },
  { key: 'online_idle_no_message_minutes', label: 'Online, no message for (min)', min: 5, max: 240,
    hint: 'Alert when a chatter is online but sends no message for this long (5–240).' },
  { key: 'payout_weekday', label: 'Payout day',
    options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    hint: 'Day of the weekly payout.' },
  { key: 'payout_hour', label: 'Payout hour (0–23)', min: 0, max: 23,
    hint: 'Hour of the payout on that day.' },
]

/**
 * Het platform waar een modelprofiel bij hoort, afgeleid uit het slug.
 *
 * Oud doet hetzelfde: `fc_` is FanCentro, `f2f_` is F2F, en wat overblijft is
 * een NAS-profiel — een map met content zonder eigen platformaccount.
 */
function platformVanSlug(slug: unknown): string {
  const s = String(slug ?? '')
  if (s.startsWith('fc_')) return 'FanCentro'
  if (s.startsWith('f2f_')) return 'F2F'
  return 'NAS'
}

/**
 * De rechten die oud als badge op een chatterkaart zet.
 *
 * Bewust niet alle veertien schakelaars: oud toont er zes, plus of iemand
 * lead is. Een rij met veertien kruisjes zegt minder dan de handvol vinkjes
 * die ertoe doen.
 */
const KAART_RECHTEN: { key: string; label: string; kind: 'ok' | 'acc' | 'warn' | 'mut' }[] = [
  { key: 'allow_mass_dm', label: 'Mass DM', kind: 'acc' },
  { key: 'allow_native_mass_dm', label: 'FC Standard DM', kind: 'acc' },
  { key: 'allow_followers', label: 'Followers', kind: 'mut' },
  { key: 'hide_earnings', label: 'No earnings', kind: 'warn' },
  { key: 'view_all_unlocks', label: 'All unlocks', kind: 'ok' },
  { key: 'allow_chatter_dashboard', label: 'Team dashboard', kind: 'acc' },
]


export default function Chatters() {
  const [tab, setTab] = useState('perf')
  const [days, setDays] = useState('1')
  const [platform, setPlatform] = useState('all')
  /** Filteren op één model of één chatter, zoals de twee keuzelijsten in oud. */
  const [model, setModel] = useState('all')
  const [chatterFilter, setChatterFilter] = useState('all')
  /** 28-10-2026: de knop Filters van oud — een eigen datumbereik (From/To)
   *  in plaats van een vast venster. Leeg = het gekozen venster. */
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [vanafDatum, setVanafDatum] = useState('')
  const [totDatum, setTotDatum] = useState('')
  const bereik = Boolean(vanafDatum && totDatum && vanafDatum <= totDatum)
  /** Zoeken in de roster, zoals "Search chatters…" in oud. */
  const [zoekChatter, setZoekChatter] = useState('')
  /** Of het scherm zichzelf elke dertig seconden bijwerkt. */
  const [autoVerversen, setAutoVerversen] = useState(true)
  /** De standaard-downloadlimiet; volgt de server tot je hem zelf aanpast. */
  const [standaardCapRuw, setStandaardCap] = useState<number | null>(null)
  /** De afwijkende limiet per chatter, zolang die nog niet opgeslagen is. */
  const [eigenCap, setEigenCap] = useState<Record<number, string>>({})
  const [presetOpen, setPresetOpen] = useState(false)
  // De drempels waarop de alarmen afgaan; oud opent die met het tandwiel
  // naast de kop van het alarmenpaneel.
  const [rulesOpen, setRulesOpen] = useState(false)
  /** De bewerkte drempels; leeg tot de dialoog opengaat. */
  const [rulesDraft, setRulesDraft] = useState<Record<string, number>>({})
  /** Toont de verzonden content over de hele geschiedenis in plaats van
   *  alleen het gekozen venster; oud heeft daar dezelfde schakelaar voor. */
  const [heleGeschiedenis, setHeleGeschiedenis] = useState(false)
  const [editing, setEditing] = useState<ChatterRights | null>(null)
  /** 30-09-2026 (Jordy): chatter "verwijderd" maken, alleen de owner. */
  const [chatterWeg, setChatterWeg] = useState<Chatter | null>(null)
  /** 2-11-2026 (Jordy): een head chatter blokkeert een eigen teamlid. */
  const [headBlok, setHeadBlok] = useState<BlokDoel | null>(null)
  const navigate = useNavigate()
  const [history, setHistory] = useState<ChatterRow | null>(null)
  /** 2-11-2026 (Jordy): de verkoop waarvan de chatter wordt gekozen, en de keuze. */
  const [toewijzen, setToewijzen] = useState<UnlockRow | null>(null)
  const [toewijsKeuze, setToewijsKeuze] = useState('')
  /** Welke dagen in History zijn uitgeklapt. */
  const [openDagen, setOpenDagen] = useState<Set<string>>(() => new Set())

  /** Rij-actie "Open": naar de chats van deze fan, zoals het oude scherm. */
  const openFan = (fan: string | null) =>
    navigate(`/conversations?fan=${encodeURIComponent(fan || '')}`)

  const { run, busy: acting } = useActions()

  // Exact dezelfde query als de bundle: platform altijd meesturen, days als
  // tijdvenster, default 1 (= Today).
  // Oud stuurt naast platform en venster ook het model en de chatter mee als
  // daarop gefilterd wordt; het endpoint kent beide parameters al.
  // De server bewaart dit overzicht zestig seconden, omdat het bij een
  // kwartaal een kwartier rekenen is. De knop Refresh moet daar dwars
  // doorheen kunnen: die zet `refresh=1` en dwingt een nieuwe berekening af.
  // De automatische verversing van 30 s doet dat bewust NIET — anders staat
  // het dashboard de hele dag hetzelfde opnieuw uit te rekenen.
  const [forceerVers, setForceerVers] = useState(false)
  const sum = useApi<Summary>(
    `/api/admin/chatter-finance/performance-summary?platform=${platform}&days=${days}`
    + (bereik ? `&from_date=${vanafDatum}&to_date=${totDatum}` : '')
    + (model !== 'all' ? `&model_id=${model}` : '')
    + (chatterFilter !== 'all' ? `&chatter_id=${chatterFilter}` : '')
    + (forceerVers ? '&refresh=1' : ''),
    [days, platform, model, chatterFilter, forceerVers, bereik, vanafDatum, totDatum])
  useEffect(() => {
    if (forceerVers && !sum.loading) setForceerVers(false)
  }, [forceerVers, sum.loading])

  /**
   * De verzonden content over de hele geschiedenis.
   *
   * Alleen opgehaald als de schakelaar aanstaat: het is dezelfde zware
   * bevraging als hierboven maar over tien jaar, en die hoort niet bij elke
   * paginabezoek te draaien.
   */
  const alles = useApi<Summary>(
    heleGeschiedenis
      // 28-10-2026: days=3650 gaf een 422 (de route accepteert ten hoogste
      // 365). Het langste venster plus alle zendingen in plaats van de eerste 100.
      ? `/api/admin/chatter-finance/performance-summary?platform=${platform}&days=365&content_history=true`
      : null,
    [heleGeschiedenis, platform])

  // De zeven kengetallen van het Productivity-blok, gerekend zoals oud: uit
  // de totalen van hetzelfde venster.
  // Hoeveel alarmen er aandacht vragen: oud telt de waarschuwingen en de
  // echte problemen, niet de informatieve meldingen.
  const teReviewen = (sum.data?.alerts ?? []).filter(
    (a) => a.tone === 'warning' || a.tone === 'warn' || a.tone === 'bad',
  ).length

  const tot = sum.data?.totals
  const uren = Number(tot?.online_minutes ?? 0) / 60
  const perUur = uren > 0 ? Number(tot?.net ?? 0) / uren : 0
  const berPerUur = uren > 0 ? Number(tot?.messages_sent ?? 0) / uren : 0
  const conversie = Number(tot?.attributed_sales ?? 0) && Number(tot?.messages_sent ?? 0)
    ? (Number(tot?.attributed_sales ?? 0) / Number(tot?.messages_sent ?? 1)) * 100
    : 0


  /** A1: wie maakte welke verkoop. Aparte route, met opzet los van de
   *  commissieberekening — daar mag deze schatting niet in meetellen. */
  const attributie = useApi<{ items: Toewijzing[] }>(
    '/api/admin/chatter-finance/sale-attribution?limit=500')
  const toewijzing = new Map<number, Toewijzing>(
    (attributie.data?.items ?? []).map((t) => [t.revenue_event_id, t]))
  const roster = useApi<Chatter[]>('/api/admin/models/chatters')
  const mij = useMij()
  /** Zoeken op naam, id of Telegram-id, zoals oud; testaccounts onderaan. */
  const rosterGezocht = useMemo(() => {
    const q = zoekChatter.trim().toLowerCase()
    return [...(roster.data ?? [])]
      .filter((c) => !q || [c.name, String(c.id), c.telegram_chat_id ?? '', c.telegram_user_id ?? '']
        .some((v) => String(v).toLowerCase().includes(q)))
      .sort((a, b) => Number(isTestAccount(a.name)) - Number(isTestAccount(b.name)))
  }, [roster.data, zoekChatter])
  const rosterTest = (roster.data ?? []).filter((c) => isTestAccount(c.name)).length
  const isOwner = String(mij.data?.role ?? '').toLowerCase() === 'owner'
  /* 30-09-2026 (Jordy): een head chatter ziet alleen de tabbladen waar hij
     het recht voor heeft — Performance met "team performance", Roster met het
     aanmaakrecht (alleen zijn eigen team; de server scoped de lijst al). */
  const beheer = isBeheer(mij.data)
  /* 28-10-2026 (Jordy): managers zien de verkopen (sales, gross, net) wél,
     maar commissie, referral, uitbetaling en wallets alleen owner/admin. De
     server haalt die velden voor alle andere rollen al weg; dit volgt dat.
     Vervangt de regel van 05-10-2026 (manager: geen omzet). */
  const beloning = isVolledigBeheer(mij.data)
  /* 2-11-2026 (Jordy): een verkoop toewijzen vanaf Chatters, zoals op Analytics
     en Earnings. POST/DELETE /sale-override is owner/admin
     (_mag_beloning_beheren); een manager of head chatter krijgt een 403 en
     ziet de knop dus niet. */
  const toewijsKnop = (u: UnlockRow) => beloning && u.id != null ? (
    <>
      {' '}
      <button className="btn ghost sm" type="button"
              onClick={() => {
                setToewijzen(u)
                setToewijsKeuze(u.chatter_id != null ? String(u.chatter_id) : '')
              }}>
        {u.chatter_id != null ? 'Change' : 'Assign'}
      </button>
    </>
  ) : null
  const zichtbareTabs = beheer ? TABS : TABS.filter((t) =>
    (t.value === 'perf' && magTeamPerformance(mij.data))
    || (t.value === 'roster' && magAccountsAanmaken(mij.data)))
  const magAanmaken = magAccountsAanmaken(mij.data)
  /* 2-11-2026 (Jordy): een head chatter mag zijn eigen team blokkeren (niet
     deblokkeren). Spiegel van `_assert_head_may_block`: alleen een actieve,
     gewone chatter met login die direct onder hem valt.
     2-11-2026 (Jordy/architect): het recht hangt aan het head-PROFIEL, dus ook
     een manager met een head-profiel. Een manager krijgt geen is_direct_report
     van de server; zijn team volgt uit lead_chatter_id. De server controleert
     het team opnieuw. Owner/admin blokkeren via de Shift agenda. */
  const mijnRol = String(mij.data?.role ?? '').toLowerCase()
  const ikBenHead = (mijnRol === 'chatter' || mijnRol === 'manager')
    && (roster.data ?? []).some((c) => c.id === mij.data?.linked_chatter_id && c.is_lead && c.active)
  const headMagBlokkeren = (c: Chatter) => ikBenHead && !c.is_lead
    && (!!c.is_direct_report
      || (c.lead_chatter_id != null && c.lead_chatter_id === mij.data?.linked_chatter_id))
    && c.active && c.has_login && c.admin_user_id != null
  useEffect(() => {
    if (mij.data && zichtbareTabs.length && !zichtbareTabs.some((t) => t.value === tab)) {
      setTab(zichtbareTabs[0].value)
    }
  }, [mij.data, tab, zichtbareTabs])
  const [nieuw, setNieuw] = useState(false)
  /** De downloadlimiet per chatter — het paneel dat oud onder dit tabblad zet. */
  const downloads = useApi<{
    limit: number
    chatters: {
      id: number; username: string; display_name: string | null
      download_count: number; blocked: boolean
      download_cap: number | null; effective_limit: number
    }[]
  }>('/api/admin/content/chatter-downloads')
  /** De modellen voor de keuzelijst; zelfde endpoint als de Chats-pagina. */
  const personas = useApi<{ id: number; slug: string; stage_name: string | null }[]>(
    '/api/admin/models/personas')
  /** 30-09-2026 (Jordy): voor Create/Edit chatter alleen actieve modellen met
   *  een platform-id — inactieve profielen en NAS-mapregels gaven "outside
   *  your assigned model scope". ModelKeuze groepeert FC en F2F per model. */
  const toewijsbaar = useApi<ModelOptie[]>(
    '/api/admin/models/personas?active_only=true&linked_only=true')
  const takeovers = useApi<{
    total_takeovers: number
    per_chatter: { chatter_id: number; name: string; takeovers: number
                   last_takeover_at: string | null
                   top_models: ({ model_slug: string; count: number } | string)[] | null }[]
  }>(`/api/admin/dashboard/takeovers-by-chatter?days=${days}`, [days])
  const mass = useApi<MassMsg[] | { data: MassMsg[] }>(
    '/api/admin/conversations/mass-message/history?limit=300')
  const massRows: MassMsg[] = Array.isArray(mass.data) ? mass.data : (mass.data?.data ?? [])

  const t = sum.data?.totals ?? {}
  const rules = sum.data?.alert_rules ?? {}
  // Alles wat de server teruggeeft, inclusief de regel met verkopen die aan
  // niemand hangen. De teller "Active chatters x / N" rekent hierop — oud las
  // bij het jaarvenster 1/23, en dat is precies de lengte van deze lijst.
  const alleRijen = sum.data?.chatters ?? []

  /**
   * Het Leaderboard is precies dat: de TOP ACHT op netto-omzet, aflopend, en
   * alleen echte chatters. Vandaar dat oud er altijd acht toont, ongeacht de
   * periode. Nieuw zette de hele lijst in de volgorde van de server neer — bij
   * het kwartaalvenster drieëntwintig rijen.
   *
   * De regel met niet-toegewezen verkopen hoort er niet in. Bij het
   * jaarvenster staat die op €283,14 netto en zou daarmee Ace (€230,60) uit de
   * top acht duwen — terwijl er niemand achter zit om te ranken. Op Earnings,
   * onder Sales per chatter, staat hij wél; daar telt hij mee in het totaal.
   */
  /**
   * 28-10-2026 (Jordy): de tellingen vastgelegd, zodat ze overal hetzelfde
   * betekenen.
   *  - Chatter: een echte chatterrij (chatter_id > 0) uit performance-summary;
   *    de regel "Unassigned sales" telt niet mee.
   *  - Testaccount: naam als "test", "test1234", "demo" of met een los woord
   *    test/demo/qa. Die tellen nergens mee en staan gemarkeerd onderaan.
   *  - Active: ONLINE nu (heartbeat ≤ 3 min), niet de vlag "actief".
   * Zo toonde de pagina "0/12 active", "8 chatters" (de vaste top acht) en
   * een detailtabel met een ander aantal, zonder dat zichtbaar was waarom.
   */
  const echteRijen = alleRijen.filter((c) => Number(c.chatter_id ?? 0) > 0)
  const telRijen = echteRijen.filter((c) => !isTestAccount(c.name))
  const aantalTest = echteRijen.length - telRijen.length
  const rows = [...telRijen]
    .sort((a, b) => Number(b.net ?? 0) - Number(a.net ?? 0))
    .slice(0, 8)

  // Elke dertig seconden bijwerken zolang de schakelaar aanstaat. De zware
  // bevraging bij een lang venster kost zestien seconden, dus dit uitzetten
  // scheelt merkbaar.
  usePoll(autoVerversen ? 30000 : 0, sum.refetch)

  const standaardCap = standaardCapRuw ?? Number(downloads.data?.limit ?? 0)

  /**
   * De modellen op id, zodat een profiel op een chatterkaart zijn echte naam
   * en platform draagt. De koppeling levert alleen het slug mee
   * ("fc_aliceimonta"); oud zoekt de nette naam net zo op.
   */
  const modelPerId = useMemo(() => {
    const m = new Map<number, { stage_name: string | null; slug: string }>()
    for (const p of personas.data ?? []) {
      m.set(Number(p.id), { stage_name: p.stage_name, slug: p.slug })
    }
    return m
  }, [personas.data])

  /** Wat de tracker toont: het venster, of de hele geschiedenis. */
  const zendingen = (heleGeschiedenis ? alles.data?.content_sends : sum.data?.content_sends) ?? []

  /** De hoogste waarde per maat; daar zet de kleurintensiteit tegen af. */
  const piek = {
    online: Math.max(1, ...alleRijen.map((c) => Number(c.online_minutes ?? 0))),
    berichten: Math.max(1, ...alleRijen.map((c) => Number(c.messages_sent ?? 0))),
    verkopen: Math.max(1, ...alleRijen.map((c) => Number(c.sales ?? 0))),
    netto: Math.max(1, ...alleRijen.map((c) => Number(c.net ?? 0))),
  }

  /**
   * De volledige lijst voor de tabellen die iedereen tonen, gesorteerd zoals
   * oud: op efficiencyscore, aflopend. Zonder die sortering stonden de
   * chatters in de volgorde van de server en las de tabel anders dan oud.
   */
  const alleChatters = [...echteRijen]
    .sort((a, b) => Number(isTestAccount(a.name)) - Number(isTestAccount(b.name))
      || Number(b.efficiency_score ?? 0) - Number(a.efficiency_score ?? 0))
  const online = telRijen.filter((c) => c.is_online).length
  /** Wie ingelogd is maar even niets doet; oud noemt dat "away". */
  const weg = telRijen.filter((c) => String(c.status ?? '').toLowerCase() === 'away').length
  /** Netto per online uur — hetzelfde getal als in het Productivity-blok. */
  const netPerUur = uren > 0 ? Number(tot?.net ?? 0) / uren : 0

  /**
   * Bij "Today" toont oud de vier grafieken per UUR, bij een langer venster
   * per DAG. Dezelfde reeksen, andere korrel.
   */
  const opUur = days === '1'
  const ch = sum.data?.charts
  const onlineReeks = (opUur ? ch?.hourly_online : ch?.daily_online) ?? []
  const omzetReeks = (opUur ? ch?.hourly_revenue : ch?.daily_revenue) ?? []
  const berichtReeks = (opUur ? ch?.hourly_messages : ch?.daily_messages) ?? []
  const reactieReeks = (opUur ? ch?.hourly_response : ch?.daily_response) ?? []

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <Seg options={RANGES} value={days} onChange={setDays} />
        <button className={filtersOpen || bereik ? 'btn sm' : 'btn ghost sm'} type="button"
                onClick={() => setFiltersOpen((v) => !v)}>
          Filters{bereik ? ' · date range' : ''}
        </button>
        {filtersOpen ? (
          <>
            <label className="mut2" style={{ fontSize: 12 }}>From{' '}
              <input className="inp sm" type="date" value={vanafDatum}
                     onChange={(e) => setVanafDatum(e.target.value)} />
            </label>
            <label className="mut2" style={{ fontSize: 12 }}>To{' '}
              <input className="inp sm" type="date" value={totDatum}
                     onChange={(e) => setTotDatum(e.target.value)} />
            </label>
            {vanafDatum || totDatum ? (
              <button className="btn ghost sm" type="button"
                      onClick={() => { setVanafDatum(''); setTotDatum('') }}>Clear date range</button>
            ) : null}
          </>
        ) : null}
        <div className="spacer" style={{ flex: 1 }} />
        {/* De twee keuzelijsten van oud: op model en op chatter. */}
        <select className="select sm" value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="all">All models</option>
          {(personas.data ?? []).map((m) => (
            <option key={m.id} value={String(m.id)}>{m.stage_name || m.slug}</option>
          ))}
        </select>
        <select className="select sm" value={chatterFilter}
                onChange={(e) => setChatterFilter(e.target.value)}>
          <option value="all">All chatters</option>
          {alleRijen.filter((c) => Number(c.chatter_id ?? 0) > 0).map((c) => (
            <option key={c.chatter_id} value={String(c.chatter_id)}>{c.name}</option>
          ))}
        </select>
        {/* Oud zet hier hoe vaak het scherm zichzelf bijwerkt, met een
            schakelaar om het uit te zetten. */}
        <label className="chkrow">
          <span className={autoVerversen ? 'chk on' : 'chk'}
                onClick={() => setAutoVerversen((v) => !v)}>
            {autoVerversen ? '✓' : ''}
          </span>
          Auto refresh: 30s
        </label>
        <button className="btn ghost sm" type="button" disabled={sum.loading}
                onClick={() => {
                  if (forceerVers) sum.refetch()
                  else setForceerVers(true)
                  takeovers.refetch()
                }}>
          {sum.loading ? 'Refreshing…' : 'Refresh'}
        </button>
        {/* KPI-regels, filter-preset en herstart zijn beheer (30-09-2026). */}
        {beheer ? (
          <>
            {/* 28-10-2026 (Jordy, 3f): KPI- en uitbetaalinstellingen opslaan is
                owner/admin; de server geeft een manager een 403. */}
            {beloning ? (
              <button className="btn ghost sm" type="button"
                      title="KPI targets & alert rules"
                      onClick={() => setRulesOpen(true)}>
                ⚙ KPI targets
              </button>
            ) : null}
            <button className="btn ghost sm" type="button" onClick={() => setPresetOpen(true)}>
              Mass DM filter preset
            </button>
            {/* 28-10-2026: uitgegrijsd, net als in Settings. De knop riep
                /api/admin/dashboard/restart-chat-service aan; die route bestaat
                op deze server niet. Chat en posting delen hier één worker, dus
                een herstart van "alleen chat" bestaat niet. */}
            <button className="btn ghost sm" type="button" disabled
                    title={'Not available on this server: chat and posting share one '
                           + 'worker, so a restart would interrupt posting. Ask Jordy.'}>
              Restart chat service
            </button>
          </>
        ) : null}
        {magAanmaken ? (
          <button className="btn primary sm" type="button"
                  onClick={() => setNieuw(true)}>
            Create chatter account
          </button>
        ) : null}
      </div>

      <div className="filters">
        <Seg options={zichtbareTabs} value={tab} onChange={setTab} />
      </div>

      {tab === 'perf' ? (
        <>
          {/* De acht kengetallen van oud, met dezelfde namen en dezelfde
              definities. Waar oud alleen een doel noemde staat hier ook hoe
              ver we zijn en welke kant het op gaat; de cijfers zijn dezelfde. */}
          <div className="grid kpis">
            <Kpi icon="A" label="Active chatters" tone="ok" value={`${online}/${telRijen.length}`}
                 meta={`${online} online now · ${weg} away`
                   + (aantalTest ? ` · ${aantalTest} test account${aantalTest === 1 ? '' : 's'} not counted` : '')} />
            <Kpi icon="T" label="Tips & unlocks" tone="acc2" value={num(t.tips_unlocks)}
                 meta={`${num(t.attributed_tips_unlocks)} chat-driven · ${num(t.unassigned_tips_unlocks)} need review`}
                 spark={vonk(sum.data?.charts?.daily_revenue, 'sales')} />
            <Kpi icon="R" label="Revenue net" tone="ok" value={eur(t.net)}
                 meta={`${eur(t.gross)} gross`}
                 chip={trend(t.net_trend_pct)}
                 target={doel(t.net, rules.revenue_net_target, eur)}
                 spark={vonk(sum.data?.charts?.daily_revenue, 'net')} />
            <Kpi icon="M" label="Messages sent" tone="acc" value={groep(t.messages_sent)}
                 meta={`${groep(t.regular_messages)} 1:1 · ${groep(t.mass_messages)} mass · ${groep(t.unlinked_human_messages)} platform-direct`}
                 spark={vonk(sum.data?.charts?.daily_messages, 'messages')} />
            <Kpi icon="A" label="Avg response" tone="warn" value={secs(t.avg_response_seconds)}
                 meta={`target ≤ ${secs(rules.response_max_seconds)}`}
                 chip={onderDoel(t.avg_response_seconds, rules.response_max_seconds)}
                 spark={vonk(sum.data?.charts?.daily_response, 'avg_response_seconds')} />
            {/* Oud noemt dit "Conversion", niet "Attribution". */}
            <Kpi icon="C" label="Conversion" tone="teal" value={pct(t.attribution_pct)}
                 meta={`${num(t.attributed_sales)} of ${num(t.sales)} sales assigned`}
                 target={doel(t.attribution_pct, rules.attribution_min_pct, (v) => pct(v))} />
            {/* En dit "Net / online hour", niet "Online". */}
            <Kpi icon="N" label="Net / online hour" tone="pink" value={eur(netPerUur)}
                 meta={`${mins(t.online_minutes)} online · ${berPerUur.toFixed(1)} msg/h`}
                 target={doel(netPerUur, rules.net_per_hour_target, eur)}
                 spark={vonk(sum.data?.charts?.daily_online, 'minutes')} />
            <Kpi icon="E" label="Efficiency score" tone="acc2"
                 value={`${num(t.avg_efficiency_score)}/100`}
                 stars={Number(t.avg_efficiency_score ?? 0) / 20}
                 meta="35% revenue · 35% reply speed · 30% messages/hour"
                 target={doel(t.avg_efficiency_score, rules.efficiency_target, (v) => num(v))} />
          </div>

          <Panel title="Leaderboard"
                 desc={`Top ${rows.length} of ${telRijen.length} chatters by net · online = heartbeat in the last 3 minutes`}
                 flush>
            {sum.loading ? <Loading /> : sum.error ? <ErrorNote error={sum.error} /> : (
              <Table cols={[
                { key: 'c', label: 'Chatter' }, { key: 's', label: 'Status' },
                { key: 'o', label: 'Online', num: true }, { key: 'm', label: '1:1 msgs', num: true },
                { key: 'md', label: 'Mass DMs', num: true }, { key: 'mr', label: 'Mass reach', num: true },
                { key: 'sa', label: 'Sales', num: true }, { key: 'g', label: 'Gross', num: true },
                { key: 'n', label: 'Net', num: true }, { key: 'cv', label: 'Conversion', num: true },
                { key: 'r', label: 'Response', num: true }, { key: 'lo', label: 'Last online' },
                { key: 'lm', label: 'Last message' },
              ]}>
                {rows.map((c) => (
                  <tr key={c.chatter_id}>
                    {/* Oud zet onder de naam wie deze chatter bedient en aan wie
                        hij rapporteert, plus een badge als hij zelf lead is. */}
                    <td style={{ minWidth: 190 }}>
                      <div className="row" style={{ gap: 6, alignItems: 'center',
                                                    flexWrap: 'wrap' }}>
                        <b>{c.name}</b>
                        {c.is_lead ? (
                          <Tag kind="acc">{beloning ? `Head ${num(c.lead_override_pct ?? 0)}%` : 'Head'}</Tag>
                        ) : null}
                      </div>
                      <div className="mut2" style={{ fontSize: 12 }}>
                        {modellenRegel(c)}
                        {c.lead_chatter_name ? ` · reports to ${c.lead_chatter_name}` : ''}
                      </div>
                    </td>
                    <td>{(() => {
                      const st = statusBadge(c.status, c.is_online)
                      return (
                        <Tag kind={st.kind}>
                          <span className={st.kind === 'ok' ? 'dotst on-live' : 'dotst off-live'} />
                          {st.label}
                        </Tag>
                      )
                    })()}</td>
                    <td className="num mono">{mins(c.online_minutes)}</td>
                    <td className="num mono">{groep(c.regular_messages)}</td>
                    {/* Oud telt hier het aantal CAMPAGNES, niet het aantal
                        verstuurde berichten: Banki staat op 2, niet op 5.072.
                        Hoeveel fans die campagnes bereikten staat ernaast
                        onder "Mass reach". */}
                    <td className="num mono">{groep(c.mass_campaigns)}</td>
                    <td className="num mono">{groep(c.mass_followers_reached)}</td>
                    <td className="num mono">{groep(c.sales)}</td>
                    <td className="num mono">{eur(c.gross)}</td>
                    <td className="num mono">{eur(c.net)}</td>
                    <td className="num mono">{pct(c.conversion_pct)}</td>
                    <td className="num mono">{secs(c.avg_response_seconds)}</td>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(c.last_seen_at)}</td>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(c.last_message_at)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {/* Oud toont elk alarm als een EIGEN kaart, niet als regels in
                één paneel: "Baki missed shift attendance", "Max response time
                high", enzovoort. */}
            {/* Oud zet de alarmen in ÉÉN paneel met de kop "Alerts" en een
                teller "n need review"; nieuw liet elke kaart los staan, waardoor
                de kop en de teller verdwenen waren. */}
            <Panel
              title="Alerts"
              right={
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <span className="mut2" style={{ fontSize: 12 }}>
                    {num(teReviewen)} need review
                  </span>
                  {beloning ? (
                    <button className="btn ghost sm" type="button" title="KPI targets & alert rules"
                            onClick={() => setRulesOpen(true)}>⚙</button>
                  ) : null}
                </div>
              }
              flush
            >
            {/* Oud zet de alarmen als LIJST neer, met een badge voor de soort
                en hoe lang geleden het speelde — niet als losse kaarten. Zo
                passen er tien in dezelfde hoogte en scrollt de rest. */}
            <div className="panelscroll" style={{ maxHeight: 260 }}>
              {!sum.data?.alerts?.length ? (
                <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
                  No alerts.
                </div>
              ) : sum.data.alerts.map((a) => {
                const s = alarmSoort(a)
                return (
                  <div className="feedrow" key={a.id}>
                    <Tag kind={s.kind}>{s.label}</Tag>
                    <span className="ftext">
                      <b>{a.title}</b>
                      <span className="mut2"> — {a.detail}</span>
                    </span>
                    <span className="mut2 ftime">{a.timestamp ? ago(a.timestamp) : ''}</span>
                  </div>
                )
              })}
            </div>
            </Panel>
            <Panel title="Activity overview">
              <div className="mini" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
                <div className="f"><small>Total online time</small><b>{mins(sum.data?.activity_overview.total_online_minutes)}</b></div>
                <div className="f"><small>Active days</small><b>{num(sum.data?.activity_overview.active_days)}</b></div>
                <div className="f"><small>Avg. session</small><b>{mins(sum.data?.activity_overview.average_session_minutes)}</b></div>
                <div className="f"><small>Online / day</small><b>{mins(sum.data?.activity_overview.average_online_minutes_per_day)}</b></div>
                <div className="f"><small>Longest session</small><b>{mins(sum.data?.activity_overview.longest_session_minutes)}</b></div>
                {/* Oud toont ook de kortste sessie en schrijft het doel als
                    "x / 8h" — hoeveel er gehaald is tegenover de norm. */}
                <div className="f"><small>Shortest session</small><b>{mins(sum.data?.activity_overview.shortest_session_minutes)}</b></div>
                <div className="f" style={{ gridColumn: '1 / -1' }}>
                  <small>Online target</small>
                  {/* Oud zet dit als balk neer: hoeveel er gehaald is tegenover
                      de norm, in één blik. Een kaal getal zegt niet of dat veel
                      of weinig is. */}
                  {(() => {
                    const gehaald = Number(
                      sum.data?.activity_overview.average_online_minutes_per_day ?? 0)
                    const norm = Number(rules.online_minutes_target ?? 0)
                    const deel = norm > 0 ? Math.min(100, (gehaald / norm) * 100) : 0
                    return (
                      <>
                        <b>{`${mins(gehaald)} / ${mins(norm)}`}</b>
                        <span className="doelbalk">
                          <span className={deel >= 100 ? 'fill ok' : 'fill'}
                                style={{ width: `${Math.max(2, deel)}%` }} />
                        </span>
                      </>
                    )
                  })()}
                </div>
              </div>
              <div className="divh" />
              <div className="mut2" style={{ fontSize: 12, marginBottom: 8 }}>
                Daily activity (last 14 days)
              </div>
              {/* Oud zet hier veertien gekleurde tegels neer met de datum en
                  de uren; de kleur zegt hoe druk die dag was. */}
              <DayTiles
                dagen={(sum.data?.activity_overview.heatmap ?? []).slice(-14)
                  .map((d) => ({ date: d.date, value: Number(d.minutes ?? 0) }))}
                format={(v) => mins(v)}
              />
            </Panel>
          </div>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Panel title="Conversion funnel" desc="From new conversation to sale">
              <FunnelChartPanel
                lagen={(sum.data?.funnel ?? []).map((f) => ({
                  label: f.label, aantal: Number(f.count ?? 0), pct: Number(f.pct ?? 0),
                }))}
              />
            </Panel>
            
          </div>

          {/* Vier grafieken over hetzelfde venster. Bij "Today" toont oud ze
              per uur, bij een langer venster per dag; de reeksen zijn
              dezelfde. */}
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Panel title={`Online hours ${opUur ? 'by hour' : 'by day'}`}
                   desc="Minutes online">
              <BarChartPanel
                labels={asLabels(onlineReeks, opUur)}
                reeksen={[{ naam: 'Online', waarden: waardenVan(onlineReeks, 'minutes'),
                            kleur: KLEUREN.ok }]}
                height={opUur ? HOOGTE.uur : HOOGTE.dag}
                format={(v) => mins(v)}
                maxLijn
              />
            </Panel>
            <Panel title={`Revenue ${opUur ? 'by hour' : 'by day'}`} desc="Net revenue">
              <LineChartPanel
                labels={asLabels(omzetReeks, opUur)}
                reeksen={[{ naam: 'Net', waarden: waardenVan(omzetReeks, 'net'),
                            kleur: KLEUREN.acc }]}
                height={opUur ? HOOGTE.uur : HOOGTE.dag}
                format={(v) => eur(v)}
              />
            </Panel>
            <Panel title={`Messages ${opUur ? 'by hour' : 'by day'}`}
                   desc="Outgoing and incoming">
              {/* Oud toont hier twee reeksen; met één lijn zie je niet of
                  een piek van ons of van de fans komt. */}
              <LineChartPanel
                labels={asLabels(berichtReeks, opUur)}
                reeksen={[
                  { naam: 'Outgoing', waarden: waardenVan(berichtReeks, 'outgoing'),
                    kleur: KLEUREN.blue },
                  { naam: 'Incoming', waarden: waardenVan(berichtReeks, 'incoming'),
                    kleur: KLEUREN.teal },
                ]}
                height={opUur ? HOOGTE.uur : HOOGTE.dag}
                format={(v) => groep(v)}
              />
            </Panel>
            <Panel title={`Response time ${opUur ? 'by hour' : 'by day'}`}
                   desc="Average time to reply">
              <LineChartPanel
                labels={asLabels(reactieReeks, opUur)}
                reeksen={[{ naam: 'Response', waarden: waardenVan(reactieReeks, 'avg_response_seconds'),
                            kleur: KLEUREN.warn }]}
                height={opUur ? HOOGTE.uur : HOOGTE.dag}
                format={(v) => secs(v)}
              />
            </Panel>
          </div>

          <Panel title="Unlocks & Tips" desc="Chat-driven sales in this window" flush>
            {(
              <Table cols={[
                { key: 't', label: 'Time' }, { key: 'p', label: 'Platform' },
                { key: 'm', label: 'Model' }, { key: 'f', label: 'Fan' },
                { key: 'ty', label: 'Type' }, { key: 'c', label: 'Chatter' },
                { key: 'a', label: 'Attribution' }, { key: 'sb', label: 'Sold by' },
                { key: 'g', label: 'Gross', num: true },
                { key: 'n', label: 'Net', num: true }, { key: 'ac', label: 'Action' },
              ]} emptyText="No chat-driven sales in this window.">
                {(sum.data?.unlocks_and_tips ?? []).map(verkoop).map((u, i) => (
                  <tr key={i}>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(u.timestamp)}</td>
                    <td><Plat value={u.platform} /></td>
                    <td><ModelNaam naam={u.model} size={18} /></td>
                    <td>{u.fan}</td>
                    <td className="mut">{u.type}</td>
                    <td>{u.chatter || <span className="dim">—</span>}</td>
                    {/* Attribution: hoe de verkoop aan deze chatter kwam, of
                        waarom er niemand aan hangt. Deze cel ontbrak, waardoor
                        Sold by, Gross en Net één kolom te vroeg stonden. */}
                    <td className="mut2" style={{ fontSize: 12 }}>
                      {u.attribution || <span className="dim">—</span>}
                      {toewijsKnop(u)}
                    </td>
                    {/* Sold by: wie de verkoop maakte volgens de attributie.
                        Staat naast "Chatter" en niet in plaats daarvan — de
                        uitbetaling blijft op de bestaande kolom gebaseerd en
                        die cijfers veranderen hier niet. De badge zegt hoe hard
                        de schatting is; hover geeft de reden. */}
                    <td>
                      {(() => {
                        const t = u.id != null ? toewijzing.get(u.id) : undefined
                        if (!t || !t.sold_by_chatter_name) {
                          return <span className="dim">—</span>
                        }
                        const kind = t.attribution_confidence === 'high' ? 'ok'
                          : t.attribution_confidence === 'medium' ? 'warn' : 'mut'
                        return (
                          <span title={t.attribution_method || undefined}>
                            {t.sold_by_chatter_name}{' '}
                            <Tag kind={kind}>{t.attribution_confidence}</Tag>
                          </span>
                        )
                      })()}
                    </td>
                    <td className="num mono">{eur(u.gross)}</td>
                    <td className="num mono">{eur(u.net)}</td>
                    <td>
                      <button className="btn ghost sm" type="button"
                              onClick={() => openFan(u.fan)}>Open</button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          {/* Toegestane afwijking van oud, met toestemming van Jordy: oud
              toont in de eerste kolom "Invalid Date" omdat het daar een veld
              leest dat niet bestaat. Wij tonen de echte datum uit
              `created_at`. */}
          <Panel title="Mass message tracker" desc={`${num(massRows.length)} campaigns`} flush>
            {/* 300 rijen zonder scroll maakte dit blok 25.341px lang; oud
                houdt het op ~500px met een eigen scrollbalk. */}
            <div className="trackerscroll">
            {mass.loading ? <Loading /> : !massRows.length ? <Empty>No mass messages</Empty> : (
              <Table cols={[
                { key: 'd', label: 'Date & time' }, { key: 'c', label: 'Chatter' },
                { key: 'p', label: 'Platform' }, { key: 'm', label: 'Model' },
                { key: 't', label: 'Text / content' }, { key: 'a', label: 'Audience' },
                { key: 's', label: 'Sent', num: true }, { key: 'f', label: 'Failed', num: true },
                { key: 'st', label: 'Status' },
              ]}>
                {massRows.map((m) => (
                  <tr key={m.id}>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(m.created_at)}</td>
                    <td>{m.created_by || <span className="dim">—</span>}</td>
                    <td><Plat value={m.platform} /></td>
                    <td><ModelNaam naam={m.model_name} size={18} /></td>
                    <td className="mut" style={{ maxWidth: 320 }}>{m.content}</td>
                    <td className="mut2" title={m.kind}>
                      {publiekTekst(m)}{m.is_paid ? ' · paid' : ''}
                    </td>
                    <td className="num mono">
                      {isNativeCampagne(m) && !Number(m.sent_count ?? 0)
                        ? <span className="mut2" style={{ fontSize: 12 }}>{verzondenTekst(m)}</span>
                        : verzondenTekst(m)}
                    </td>
                    <td className="num mono">{num(m.failed_count)}</td>
                    {/* Oud zet het aantal mislukten achter de status —
                        "cancelled · 140 failed" — zodat een half gelukte
                        campagne niet als een gewone afsluiting leest. */}
                    <td>
                      <Tag kind={m.status === 'done' && !moetNaversturen(m) ? 'ok'
                        : m.failed_count || moetNaversturen(m) ? 'bad' : 'warn'}>
                        {isNativeCampagne(m) && m.status === 'done' ? 'accepted' : m.status}
                        {m.failed_count ? ` · ${num(m.failed_count)} failed` : ''}
                      </Tag>
                      {/* 28-10-2026 (Jordy): de foutregel van oud, met de weg
                          naar "Resend remaining". In v2 staat versturen onder
                          Mass Messages; een aparte knop "Resend remaining" is
                          daar (nog) niet gevonden. */}
                      {moetNaversturen(m) ? (
                        <div className="mut2" style={{ fontSize: 11.5, color: '#fca5a5', marginTop: 3 }}>
                          {String(m.status).toLowerCase() === 'failed' && !Number(m.sent_count ?? 0)
                            ? 'Nothing was sent. Resend via '
                            : 'Incomplete. Send the rest via '}
                          <a href="/mass-messages" onClick={(e) => { e.preventDefault(); navigate('/mass-messages') }}>
                            Mass Messages
                          </a>
                          {' '}(Resend remaining; keep “exclude recent” on).
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
          </Panel>

          <Panel
            title="Sent content tracker"
            desc={`${groep(zendingen.length)} sends`}
            right={
              <label className="chkrow">
                <span className={heleGeschiedenis ? 'chk on' : 'chk'}
                      onClick={() => setHeleGeschiedenis((v) => !v)}>
                  {heleGeschiedenis ? '✓' : ''}
                </span>
                Last 365 days
              </label>
            }
            flush
          >
            {(
              <Table cols={[
                { key: 't', label: 'Time' }, { key: 'c', label: 'Chatter' },
                { key: 'p', label: 'Platform' }, { key: 'm', label: 'Model' },
                { key: 'f', label: 'Fan' }, { key: 'ct', label: 'Content' },
                { key: 'a', label: 'Access' }, { key: 'pr', label: 'Price', num: true },
                { key: 'd', label: 'Delivery' }, { key: 's', label: 'Sale', num: true },
                { key: 'o', label: 'Open' },
              ]}>
                {(zendingen).map((c, i) => (
                  <tr key={i}>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(c.timestamp)}</td>
                    <td>{c.chatter || <span className="dim">—</span>}</td>
                    <td><Plat value={c.platform} /></td>
                    <td><ModelNaam naam={c.model} size={18} /></td>
                    <td>{c.fan}</td>
                    <td className="mut" style={{ maxWidth: 240 }}>{c.preview || '—'}</td>
                    <td>{c.is_paid ? <Tag kind="warn">Paid</Tag> : <Tag kind="mut">Free</Tag>}</td>
                    {/* FanCentro rekent in credits, F2F in euro's — dezelfde
                        regel als in het chatvenster. */}
                    <td className="num mono">
                      {c.price != null && c.price > 0
                        ? (String(c.platform).toLowerCase() === 'f2f'
                            ? eur(c.price) : `${groep(c.price)} credits`)
                        : '—'}
                    </td>
                    <td>{c.delivered
                      ? <Tag kind="ok">Delivered</Tag>
                      : <Tag kind="bad">{c.delivery_error || 'Failed'}</Tag>}</td>
                    <td className="num mono">{c.sale_net != null ? eur(c.sale_net) : '—'}</td>
                    <td>
                      <button className="btn ghost sm" type="button"
                              onClick={() => openFan(c.fan)}>Open</button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          <Panel title="Live activity" desc="Latest messages and sales" flush>
            <div className="listscroll" style={{ maxHeight: 420 }}>
            {!sum.data?.live_activity?.length ? <Empty>No activity</Empty> : (
              <div className="feed">
                {sum.data.live_activity.slice(0, 40).map((a, i) => (
                  <div className="it" key={i}>
                    <span className={`st tag ${a.is_paid ? 'ok' : 'acc'}`}>{a.type}</span>
                    <div>
                      <b>{activiteitRegel(a)}</b>
                      {a.preview ? (
                        <div className="mut" style={{ marginTop: 2 }}>{a.preview}</div>
                      ) : null}
                    </div>
                    <span className="tm">{dt(a.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          </Panel>

          <Panel title="Latest chatter sales" flush>
            {(
              <Table cols={[
                { key: 't', label: 'Time' }, { key: 'f', label: 'Fan' },
                { key: 'm', label: 'Model' }, { key: 'ty', label: 'Type' },
                { key: 'p', label: 'Platform' }, { key: 'c', label: 'Chatter' },
                { key: 'a', label: 'Attribution' }, { key: 'sb', label: 'Sold by' },
                { key: 'g', label: 'Gross', num: true },
                { key: 'n', label: 'Net', num: true }, { key: 'o', label: 'Open' },
              ]}>
                {(sum.data?.latest_transactions ?? []).map(verkoop).map((u, i) => (
                  <tr key={i}>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(u.timestamp)}</td>
                    <td>{u.fan}</td>
                    <td><ModelNaam naam={u.model} size={18} /></td>
                    <td className="mut">{u.type}</td>
                    <td><Plat value={u.platform} /></td>
                    <td>{u.chatter || <span className="dim">—</span>}</td>
                    {/* Attribution: hoe de verkoop aan deze chatter kwam, of
                        waarom er niemand aan hangt. Deze cel ontbrak, waardoor
                        Sold by, Gross en Net één kolom te vroeg stonden. */}
                    <td className="mut2" style={{ fontSize: 12 }}>
                      {u.attribution || <span className="dim">—</span>}
                      {toewijsKnop(u)}
                    </td>
                    {/* Sold by: wie de verkoop maakte volgens de attributie.
                        Staat naast "Chatter" en niet in plaats daarvan — de
                        uitbetaling blijft op de bestaande kolom gebaseerd en
                        die cijfers veranderen hier niet. De badge zegt hoe hard
                        de schatting is; hover geeft de reden. */}
                    <td>
                      {(() => {
                        const t = u.id != null ? toewijzing.get(u.id) : undefined
                        if (!t || !t.sold_by_chatter_name) {
                          return <span className="dim">—</span>
                        }
                        const kind = t.attribution_confidence === 'high' ? 'ok'
                          : t.attribution_confidence === 'medium' ? 'warn' : 'mut'
                        return (
                          <span title={t.attribution_method || undefined}>
                            {t.sold_by_chatter_name}{' '}
                            <Tag kind={kind}>{t.attribution_confidence}</Tag>
                          </span>
                        )
                      })()}
                    </td>
                    <td className="num mono">{eur(u.gross)}</td>
                    <td className="num mono">{eur(u.net)}</td>
                    <td>
                      <button className="btn ghost sm" type="button"
                              onClick={() => openFan(u.fan)}>Open</button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          {/* Oud toont onder Productivity ZEVEN kengetallen, geen tabel.
              De brede tabel die hier stond hoort onder "Detailed chatter
              performance"; die stond er dubbel, met andere kolomnamen. */}
          <Panel title="Productivity">
            {sum.loading ? <Loading /> : (
              <div className="kpis">
                <Kpi label="Online hours per day" value={`${uren.toFixed(1)}h`} />
                <Kpi label="Net per online hour" value={eur(perUur)} />
                <Kpi label="Messages per hour" value={berPerUur.toFixed(1)} />
                <Kpi label="Conversion chats to sales" value={pct(conversie)} />
                <Kpi label="Reaction time" value={secs(tot?.avg_response_seconds)} />
                {beloning ? <Kpi label="Open commission" value={eur(tot?.open_commission)} /> : null}
                {beloning ? <Kpi label="Referral earned" value={eur(tot?.referral_earned)} /> : null}
              </div>
            )}
          </Panel>

          <div className="grid" style={{ gridTemplateColumns: '1.2fr 1fr' }}>
            <Panel title="Revenue overview" desc="This period vs previous period">
              {/* Oud legt de vorige periode ernaast; zonder die tweede lijn
                  zegt een omzetgrafiek niets over of het beter of slechter
                  gaat. */}
              <LineChartPanel
                labels={asLabels(sum.data?.charts?.daily_revenue ?? [], false)}
                reeksen={[
                  { naam: 'This period',
                    waarden: waardenVan(sum.data?.charts?.daily_revenue ?? [], 'net'),
                    kleur: KLEUREN.acc },
                  { naam: 'Previous period',
                    waarden: waardenVan(sum.data?.charts?.previous_daily_revenue ?? [], 'net'),
                    kleur: KLEUREN.teal },
                ]}
                format={(v) => eur(v)}
              />
            </Panel>
            <Panel title="Platform and model mix" desc="Where the revenue comes from">
              {/* Oud toont dit als de verdeling van de OMZET over platform en
                  model, met het bedrag erbij — niet als een balkje per chatter
                  zonder getal. */}
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <DonutChartPanel
                  delen={(sum.data?.platform_mix ?? []).map((p) => ({
                    naam: String(p.platform).toLowerCase() === 'f2f' ? 'F2F' : 'FanCentro',
                    waarde: Number(p.net ?? 0),
                    kleur: String(p.platform).toLowerCase() === 'f2f'
                      ? KLEUREN.blue : KLEUREN.pink,
                  }))}
                  format={(v) => eur(v)}
                  midden={eur(tot?.net)}
                />
                <div className="panelscroll" style={{ maxHeight: HOOGTE.mix }}>
                  {!isBeheer(mij.data) ? (
                    <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
                      Revenue per model is visible to the agency only
                    </div>
                  ) : (sum.data?.model_mix ?? []).length === 0 ? (
                    <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
                      No data in this window
                    </div>
                  ) : (sum.data?.model_mix ?? []).map((m) => (
                    <div className="bar" key={m.model_persona_id}>
                      <span className="mut">{m.model_name}</span>
                      <div className="track">
                        <div className="fill"
                             style={{ width: `${Math.max(2, Number(m.share_pct ?? 0))}%` }} />
                      </div>
                      <span className="num mono">{eur(m.net)}</span>
                      <span className="mut2" style={{ fontSize: 12, minWidth: 46,
                                                      textAlign: 'right' }}>
                        {pct(m.share_pct)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </div>

          

          {/* Oud heeft naast Productivity nog een breed prestatie-overzicht
              en een apart blok met de online-tijd per chatter. */}
          <Panel title="Detailed chatter performance" flush>
            {sum.loading ? <Loading /> : (
              <Table cols={[
                { key: 'c', label: 'Chatter' }, { key: 'st', label: 'Status' },
                { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
                { key: 'gn', label: 'Net % gross', num: true }, { key: 'ts', label: 'Team share', num: true },
                { key: 's', label: 'Sales', num: true }, { key: 'r', label: 'Response', num: true },
                { key: 'sc', label: 'Score', num: true }, { key: 'o', label: 'Online', num: true },
                { key: 'eh', label: '€/h', num: true }, { key: 'mh', label: 'Msgs/h', num: true },
                { key: 'cut', label: 'Chatter cut', num: true }, { key: 'ref', label: 'Referral', num: true },
                { key: 'due', label: 'Total due', num: true }, { key: 'op', label: 'Open' },
                { key: 'mix', label: 'Platform / model mix' },
              ].filter((k) => beloning || !BELONINGKOLOMMEN_DETAIL.includes(k.key))}>
                {alleChatters.map((c) => (
                  <tr key={c.chatter_id}>
                    <td><b>{c.name}</b>{isTestAccount(c.name) ? <> <Tag kind="warn">Test</Tag></> : null}</td>
                    <td>{(() => {
                      const st = statusBadge(c.status, c.is_online)
                      return (
                        <Tag kind={st.kind}>
                          <span className={st.kind === 'ok' ? 'dotst on-live' : 'dotst off-live'} />
                          {st.label}
                        </Tag>
                      )
                    })()}</td>
                    <td className="num mono">{eur(c.gross)}</td>
                    <td className="num mono">{eur(c.net)}</td>
                    <td className="num mono">{pct(c.gross_to_net_pct)}</td>
                    <td className="num mono">{pct(c.share_of_attributed_net_pct)}</td>
                    <td className="num mono">{num(c.sales)}</td>
                    <td className="num mono">{secs(c.avg_response_seconds)}</td>
                    <td className="num mono">{num(c.efficiency_score)}</td>
                    <td className="num mono">{mins(c.online_minutes)}</td>
                    <td className="num mono">{eur(c.net_per_hour)}</td>
                    <td className="num mono">{Number(c.messages_per_hour ?? 0).toFixed(1)}</td>
                    {beloning ? <td className="num mono">{eur(c.own_commission)}</td> : null}
                    {beloning ? <td className="num mono">{eur(c.referral_earned)}</td> : null}
                    {/* "Total due" is wat deze chatter in totaal verdiende,
                        "Open" wat daarvan nog openstaat — een bedrag, geen
                        knop. */}
                    {beloning ? <td className="num mono">{eur(c.total_earned)}</td> : null}
                    {beloning ? <td className="num mono">{eur(c.open_commission)}</td> : null}
                    {/* En waar die omzet vandaan komt: per platform en per model,
                        met bedrag en aandeel. 28-10-2026 (Jordy): hier stond
                        "0: [object Object]". De platformlijst werd als object
                        gelezen en kreeg een eigen cel ZONDER kop, waardoor de
                        rij één cel langer was dan de koppen. Nu één cel. */}
                    <td style={{ minWidth: 240 }}>
                      {(c.platform_breakdown ?? []).length === 0
                        && (c.model_breakdown ?? []).length === 0 ? (
                        <span className="dim">—</span>
                      ) : (
                        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                          {(c.platform_breakdown ?? []).map((p) => (
                            <Tag kind="acc" key={`p-${p.platform}`}>
                              {String(p.platform).toLowerCase() === 'f2f' ? 'F2F' : 'FanCentro'}
                              {' '}{eur(p.net)} · {pct(p.share_pct)}
                            </Tag>
                          ))}
                          {(c.model_breakdown ?? []).slice(0, 4).map((m) => (
                            <Tag kind="mut" key={`m-${m.model_persona_id}`}>
                              {m.model_name} {eur(m.net)} · {pct(m.share_pct)}
                            </Tag>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          <Panel title="Chatter online time"
                 desc="Online per chatter in this window; History shows the days." flush>
            <div className="listscroll" style={{ maxHeight: 370 }}>
              <Table cols={[
                { key: 'c', label: 'Chatter' }, { key: 't', label: 'Total', num: true },
                { key: 'd', label: 'Active days', num: true },
                { key: 's', label: 'Status' }, { key: 'l', label: 'Last session' },
                { key: 'a', label: 'History' },
              ]} emptyText="No online time recorded.">
                {(sum.data?.chatters ?? []).map((c) => (
                  <tr key={c.chatter_id}>
                    <td><b>{c.name}</b>{isTestAccount(c.name) ? <> <Tag kind="warn">Test</Tag></> : null}</td>
                    <td className="num mono">{mins(c.online_minutes)}</td>
                    <td className="num mono">
                      {num((c.daily_activity ?? []).filter((d) => d.minutes > 0).length)}
                    </td>
                    <td>{c.is_online ? <Tag kind="ok">Online</Tag>
                      : <Tag kind="mut">{c.status || 'Offline'}</Tag>}</td>
                    <td className="mut2" style={{ fontSize: 12 }}>{dt(c.last_seen_at)}</td>
                    <td>
                      <button className="btn ghost sm" type="button"
                              onClick={() => setHistory(c)}>History</button>
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          </Panel>

          <Panel title="Takeovers per chatter"
                   desc={`${groep(takeovers.data?.total_takeovers)} takeovers · claim, another chatter, first reply after 6h, AI handoff`}
                   flush>
              <Table cols={[
                { key: 'c', label: 'Chatter' }, { key: 't', label: 'Takeovers', num: true },
                { key: 'l', label: 'Last' }, { key: 'm', label: 'Top models' },
              ]}>
                {(takeovers.data?.per_chatter ?? []).map((c) => (
                  <tr key={c.chatter_id}>
                    <td>{c.name}</td>
                    <td className="num mono">{num(c.takeovers)}</td>
                    <td className="mut2" style={{ fontSize: 12 }}>
                      {c.last_takeover_at ? dt(c.last_takeover_at) : '—'}
                    </td>
                    <td className="mut" style={{ fontSize: 12 }}>
                      {(c.top_models ?? []).map((n) => (typeof n === 'string' ? n : n.model_slug)).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </Table>
            </Panel>

          {/* Oud toont hier een MATRIX met gekleurde balken per chatter, niet
              nog een keer de tabel met online-tijd. Vier maten naast elkaar —
              online, berichten, verkopen en netto — waarbij de kleurintensiteit
              zegt hoe die chatter zich tot de rest verhoudt. Zo zie je in één
              blik wie waar sterk in is. */}
          <Panel title="Activity matrix"
                 desc="Online, messages, sales and net per chatter" flush>
            {sum.loading ? <Loading /> : (
              <div className="matrix">
                <div className="kop">
                  <span>Chatter</span>
                  <span>Online</span>
                  <span>1:1 · mass · fans</span>
                  <span>Sales</span>
                  <span>Net</span>
                </div>
                <div className="panelscroll" style={{ maxHeight: 340 }}>
                  {alleChatters.length === 0 ? (
                    <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
                      No chatter data in this window.
                    </div>
                  ) : alleChatters.map((c) => (
                    <div className="rij" key={c.chatter_id}>
                      <span className="naam">{c.name}</span>
                      <MatrixCel waarde={Number(c.online_minutes ?? 0)}
                                 piek={piek.online} kleur={KLEUREN.ok}
                                 tekst={mins(c.online_minutes)} />
                      <MatrixCel waarde={Number(c.messages_sent ?? 0)}
                                 piek={piek.berichten} kleur={KLEUREN.blue}
                                 tekst={`${groep(c.regular_messages)} · ${groep(c.mass_messages)} · ${groep(c.mass_followers_reached)}`} />
                      <MatrixCel waarde={Number(c.sales ?? 0)}
                                 piek={piek.verkopen} kleur={KLEUREN.warn}
                                 tekst={groep(c.sales)} />
                      <MatrixCel waarde={Number(c.net ?? 0)}
                                 piek={piek.netto} kleur={KLEUREN.acc}
                                 tekst={eur(c.net)} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        </>
      ) : null}

      {/* 28-10-2026 (Jordy): de volledige Shift agenda van oud — berichten en
          blokkeren, diensten en reeksen beheren, weekkalender, geschiedenis
          en de Telegram-waarschuwingen. Zie components/ShiftAgenda. */}
      {tab === 'shift' ? <ShiftAgenda /> : null}

      {tab === 'roster' ? (
        <>
          {/* Oud zet onder dit tabblad de DOWNLOADLIMIET: hoeveel content een
              chatter in een voortschrijdend etmaal mag ophalen, en het
              terugzetten van wie daarop is vastgelopen. Dat paneel ontbrak
              hier volledig. De accounttabellen eronder houdt dit dashboard
              erbij; oud toont die onder Team setup. */}
          {/* 23-10-2026 (Content fase 2): terugzetten en grenzen wijzigen is
              owner/admin (server: require_owner_or_admin); een manager mag zijn
              eigen exfiltratierem niet resetten. */}
          {isVolledigBeheer(mij.data) ? (
          <Panel
            title="Chatter downloads"
            desc="Set the rolling 24-hour content download limit and reset a blocked chatter."
            right={
              <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                <span className="mut2" style={{ fontSize: 12 }}>Default cap</span>
                <input
                  className="inp sm"
                  type="number"
                  style={{ width: 110 }}
                  value={standaardCap}
                  onChange={(e) => setStandaardCap(Math.max(0, Number(e.target.value || 0)))}
                />
                <button
                  className="btn sm"
                  type="button"
                  disabled={acting === 'Save default cap'}
                  onClick={() => run({
                    label: 'Save default cap',
                    // 23-10-2026: de server leest `limit` uit de URL; in de body
                    // kwam hij nooit aan (422).
                    request: mut.post(`/api/admin/content/chatter-downloads/limit?limit=${
                      encodeURIComponent(String(Number(standaardCap) || 0))}`),
                    onDone: downloads.refetch,
                    successText: 'Default cap saved.',
                  })}
                >
                  Save default
                </button>
              </div>
            }
            flush
          >
            {downloads.loading ? <Loading /> : (
              <Table cols={[
                { key: 'c', label: 'Chatter' },
                { key: 'd', label: 'Downloads', num: true },
                { key: 'e', label: 'Effective cap', num: true },
                { key: 's', label: 'Status' },
                { key: 'cc', label: 'Custom cap', num: true },
                { key: 'a', label: 'Actions' },
              ]} emptyText="No chatters.">
                {(downloads.data?.chatters ?? []).map((c) => (
                  <tr key={c.id}>
                    <td><b>{c.display_name || c.username}</b></td>
                    <td className="num mono">{groep(c.download_count)}</td>
                    <td className="num mono">{groep(c.effective_limit)}</td>
                    <td>{c.blocked
                      ? <Tag kind="bad">Blocked</Tag>
                      : <Tag kind="ok">Available</Tag>}</td>
                    {/* Oud laat de eigen limiet hier invullen, met "Default"
                        als tijdelijke tekst zolang er geen afwijkende waarde
                        staat. Leeg opslaan zet de chatter terug op de
                        standaard. */}
                    <td>
                      <input
                        className="inp sm"
                        type="number"
                        style={{ width: 90 }}
                        placeholder="Default"
                        value={eigenCap[c.id] ?? (c.download_cap != null
                          ? String(c.download_cap) : '')}
                        onChange={(e) => setEigenCap((v) => ({
                          ...v, [c.id]: e.target.value,
                        }))}
                      />
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <button className="btn ghost sm" type="button"
                                disabled={acting === 'Save cap'}
                                onClick={() => run({
                                  label: 'Save cap',
                                  // 23-10-2026: in de URL (de server leest de
                                  // query). In de body werd limit 0 en wiste
                                  // "Save cap" de eigen grens stil. Leeg = terug
                                  // naar de standaard (clear=true).
                                  request: mut.post(
                                    (eigenCap[c.id] ?? '') === ''
                                      ? `/api/admin/content/chatter-downloads/${c.id}/limit?clear=true`
                                      : `/api/admin/content/chatter-downloads/${c.id}/limit?limit=${
                                          encodeURIComponent(String(Number(eigenCap[c.id]) || 0))}`),
                                  onDone: downloads.refetch,
                                  successText: 'Cap saved.',
                                })}>
                          Save cap
                        </button>
                        <button className="btn ghost sm" type="button"
                                disabled={acting === 'Reset downloads'}
                                onClick={() => run({
                                  label: 'Reset downloads',
                                  request: mut.post(
                                    `/api/admin/content/chatter-downloads/${c.id}/reset`),
                                  onDone: downloads.refetch,
                                  successText: 'Download counter reset.',
                                })}>
                          Reset
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>
          ) : null}

          {/* Oud toont de chatters hier als KAART per chatter, niet als drie
              losse tabellen. Op één kaart staat alles wat over die chatter
              gaat: id, naam, of het account aanstaat, hoeveel modellen en
              platformprofielen hij bedient met de namen erbij, wanneer hij
              voor het laatst actief was, welke rechten aanstaan en of hij
              lead is of aan iemand rapporteert.

              Met drie tabellen naast elkaar moest je per chatter drie keer
              zoeken; dat is precies wat oud vermijdt. */}
          <Panel
            title="Chatters"
            desc={`${groep(rosterGezocht.length)} of ${groep((roster.data ?? []).length)} chatters`
              + (rosterTest ? ` · ${rosterTest} test account${rosterTest === 1 ? '' : 's'} marked` : '')}
            right={
              <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                <input className="inp sm" style={{ width: 200 }} placeholder="Search chatters…"
                       value={zoekChatter} onChange={(e) => setZoekChatter(e.target.value)} />
                {magAanmaken ? (
                  <button className="btn primary sm" type="button"
                          onClick={() => setNieuw(true)}>
                    Add Chatter Account
                  </button>
                ) : null}
              </div>
            }
          >
            {roster.loading ? <Loading /> : roster.error ? <ErrorNote error={roster.error} /> : (
              <div className="chatterkaarten">
                {rosterGezocht.length === 0 ? (
                  <div className="mut2" style={{ fontSize: 12 }}>No chatters.</div>
                ) : rosterGezocht.map((c) => (
                  <div className="chatterkaart" key={c.id}>
                    <div className="kop">
                      <span className="id mono">{c.id}</span>
                      <b>{c.name}</b>
                      {isTestAccount(c.name) ? <Tag kind="warn">Test account</Tag> : null}
                      {c.account_active
                        ? <Tag kind="ok">Active</Tag>
                        : headMagBlokkeren(c)
                          ? <Tag kind="bad">Blocked</Tag>
                          : <Tag kind="mut">Inactive</Tag>}
                      {c.is_monitor_only ? <Tag kind="warn">Monitor only</Tag> : null}
                      <span className="spacer" style={{ flex: 1 }} />
                      {/* Bewerken is beheer; een head chatter krijgt daar een 403. */}
                      {beheer ? (
                        <button className="btn ghost sm" type="button"
                                onClick={() => setEditing(c as unknown as ChatterRights)}>
                          Edit
                        </button>
                      ) : null}
                      {isOwner ? (
                        <button className="btn danger sm" type="button"
                                onClick={() => setChatterWeg(c)}>
                          Delete
                        </button>
                      ) : null}
                      {headMagBlokkeren(c) && c.account_active ? (
                        <button className="btn danger sm" type="button"
                                onClick={() => setHeadBlok({
                                  admin_user_id: Number(c.admin_user_id),
                                  display_name: c.name, username: c.name,
                                })}>
                          Block
                        </button>
                      ) : null}
                    </div>

                    <div className="kv">
                      <span>Telegram Chat ID</span>
                      <span className="mono">{c.telegram_chat_id || '—'}</span>
                      <span>Login</span>
                      <span>{c.has_login ? 'Yes' : 'No'}</span>
                      <span>Last Active</span>
                      <span>{c.last_active_at
                        ? new Date(String(c.last_active_at)).toLocaleString('en-GB')
                        : 'Never'}</span>
                    </div>

                    <div className="veld">
                      <span className="lbl">Assigned Models / Profiles</span>
                      <span className="mut2" style={{ fontSize: 12 }}>
                        {modellenRegel(c)}
                      </span>
                      <div className="chips">
                        {(c.assigned_models ?? []).length === 0 ? (
                          <span className="dim" style={{ fontSize: 12 }}>None</span>
                        ) : (c.assigned_models ?? []).map((m, i) => {
                          const persona = modelPerId.get(Number(m.model_persona_id))
                          const naam = persona?.stage_name
                            || m.model_name || m.model_slug || `#${m.model_persona_id}`
                          const plat = platformVanSlug(persona?.slug || m.model_slug)
                          return (
                            <Tag kind={m.is_primary ? 'acc' : 'mut'}
                                 key={`${m.model_persona_id}-${i}`}>
                              {naam} · {plat}{m.is_primary ? ' *' : ''}
                            </Tag>
                          )
                        })}
                      </div>
                    </div>

                    <div className="veld">
                      <span className="lbl">Permissions</span>
                      <div className="chips">
                        {!c.has_login ? (
                          <Tag kind="mut">no login</Tag>
                        ) : (
                          <>
                            {KAART_RECHTEN.filter(
                              (r) => Boolean((c as unknown as Record<string, boolean>)[r.key]),
                            ).map((r) => (
                              <Tag kind={r.kind} key={r.key}>{r.label}</Tag>
                            ))}
                            {c.is_lead ? (
                              <Tag kind="acc">{beloning ? `Head ${num(c.lead_override_pct ?? 0)}%` : 'Head'}</Tag>
                            ) : null}
                            {c.lead_chatter_id ? (
                              <Tag kind="mut">
                                Reports to {(roster.data ?? [])
                                  .find((x) => x.id === c.lead_chatter_id)?.name ?? '—'}
                              </Tag>
                            ) : null}
                            {beloning && c.lead_chatter_id && c.referral_since ? (
                              <Tag kind="mut">Referral since {utcLabel(c.referral_since)}</Tag>
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      ) : null}

      {rulesOpen ? (
        <Modal
          title="KPI targets & alert rules"
          desc="These thresholds decide when an alert fires and what the targets on the KPI cards are measured against."
          width={620}
          onClose={() => setRulesOpen(false)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setRulesOpen(false)}>Cancel</button>
              <button className="btn primary sm" type="button"
                      disabled={acting === 'Save alert rules'}
                      onClick={() => run({
                        label: 'Save alert rules',
                        request: mut.put('/api/admin/chatter-finance/alert-rules',
                                         { ...(rules as Record<string, number>), ...rulesDraft }),
                        onDone: () => { setRulesOpen(false); sum.refetch() },
                        successText: 'Targets saved.',
                      })}>
                Save
              </button>
            </>
          }
        >
          <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {DREMPELS.map((d) => (
              <label className="field" key={d.key}>
                <span>{d.label}</span>
                {/* 2-11-2026 (Jordy): een keuzelijst voor de uitbetaaldag. */}
                {d.options ? (
                  <select
                    className="inp"
                    value={String(rulesDraft[d.key] ?? (rules as Record<string, number>)[d.key] ?? '')}
                    onChange={(e) => setRulesDraft((v) => ({
                      ...v, [d.key]: Number(e.target.value),
                    }))}
                  >
                    {d.options.map((o, i) => <option key={o} value={i}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    className="inp"
                    type="number"
                    step={d.step ?? 1}
                    min={d.min}
                    max={d.max}
                    value={String(rulesDraft[d.key] ?? (rules as Record<string, number>)[d.key] ?? '')}
                    onChange={(e) => setRulesDraft((v) => ({
                      ...v, [d.key]: Number(e.target.value),
                    }))}
                  />
                )}
                <span className="hint">{d.hint}</span>
              </label>
            ))}
          </div>
        </Modal>
      ) : null}

      {/* 2-11-2026 (Jordy): Assign / Change bij Unlocks & Tips en Latest chatter
          sales. Dezelfde aanroepen als Earnings: een chatter is een POST, "terug
          naar de automatische regel" een DELETE. Daarna het overzicht opnieuw
          laten berekenen (refresh=1, de server bewaart het anders 60 s). */}
      {toewijzen ? (
        <Modal
          title={toewijzen.chatter_id != null ? 'Change sale assignment' : 'Assign sale'}
          desc={`${toewijzen.type} of ${eur(toewijzen.gross)} from ${toewijzen.fan || 'unknown fan'}`
            + ` on ${dt(toewijzen.timestamp)}. A manual assignment overrides the automatic attribution.`}
          width={460}
          onClose={() => setToewijzen(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setToewijzen(null)}>Cancel</button>
              <button className="btn primary sm" type="button"
                      disabled={!toewijsKeuze || acting === 'Assign sale'}
                      onClick={() => run({
                        label: 'Assign sale',
                        request: toewijsKeuze === 'auto'
                          ? mut.del(`/api/admin/chatter-finance/sale-override/${toewijzen.id}`)
                          : mut.post('/api/admin/chatter-finance/sale-override', {
                              revenue_event_id: toewijzen.id,
                              chatter_id: Number(toewijsKeuze),
                              reason: 'Assigned from Chatters: manual assignment',
                            }),
                        onDone: () => {
                          setToewijzen(null)
                          attributie.refetch()
                          if (forceerVers) sum.refetch()
                          else setForceerVers(true)
                        },
                        successText: toewijsKeuze === 'auto'
                          ? 'Back to the automatic rule.' : 'Sale assignment saved.',
                      })}>
                Save
              </button>
            </>
          }
        >
          <label className="field">
            <span>Chatter</span>
            <select className="inp" value={toewijsKeuze}
                    onChange={(e) => setToewijsKeuze(e.target.value)}>
              <option value="">— choose a chatter —</option>
              <option value="auto">Back to the automatic rule</option>
              {(roster.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        </Modal>
      ) : null}

      {presetOpen ? <MassDmPresetDialog onClose={() => setPresetOpen(false)} /> : null}
      {history ? (
        <Modal
          title={`Activity — ${history.name}`}
          desc="Sessions per day in the selected period."
          width={620}
          onClose={() => setHistory(null)}
        >
          {/* Oud klapt hier per dag de losse sessies uit, met het dagtotaal
              als badge. Een dag met vier korte sessies leest heel anders dan
              een dag met één lange, en dat verschil verdween in een kale
              minutenkolom. */}
          {(history.daily_activity ?? []).filter((d) => d.minutes > 0).length === 0 ? (
            <div className="mut2" style={{ fontSize: 12 }}>No activity in this period.</div>
          ) : (
            <div className="panelscroll" style={{ maxHeight: 420 }}>
              {(history.daily_activity ?? [])
                .filter((d) => d.minutes > 0)
                .map((d) => (
                  <div className="dagrij" key={d.date}>
                    {/* 28-10-2026: uitklapbaar per dag, zoals oud. */}
                    <div className="kop" role="button" tabIndex={0}
                         style={{ cursor: 'pointer' }}
                         onClick={() => setOpenDagen((v) => {
                           const n = new Set(v)
                           if (n.has(d.date)) n.delete(d.date); else n.add(d.date)
                           return n
                         })}>
                      <span className="mut2">{openDagen.has(d.date) ? '▾' : '▸'}</span>
                      <b className="mono">{d.date}</b>
                      <Tag kind="ok">{mins(d.minutes)}</Tag>
                      <span className="mut2" style={{ fontSize: 12 }}>
                        {num((d.sessions ?? []).length)} session
                        {(d.sessions ?? []).length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {openDagen.has(d.date) ? <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                      {(d.sessions ?? []).length === 0 ? (
                        <span className="dim" style={{ fontSize: 12 }}>
                          No sessions recorded
                        </span>
                      ) : (d.sessions ?? []).map((se, i) => (
                        <Tag kind="mut" key={i}>
                          {klok(se.clock_in)}–{klok(se.clock_out)} · {mins(se.minutes)}
                        </Tag>
                      ))}
                    </div> : null}
                  </div>
                ))}
            </div>
          )}
        </Modal>
      ) : null}

      {nieuw ? (
        <ChatterAccountDialog
          isOwner={isOwner}
          leads={(roster.data ?? []).filter((c) => c.is_lead && c.active)
            .map((c) => ({ id: c.id, name: c.name, lead_override_pct: c.lead_override_pct ?? null }))}
          personas={toewijsbaar.data ?? []}
          onClose={() => setNieuw(false)}
          onSaved={roster.refetch}
        />
      ) : null}

      {chatterWeg ? (
        <VerwijderDialog
          titel="Delete chatter"
          naam={chatterWeg.name}
          request={mut.del(`/api/admin/models/chatters/${chatterWeg.id}`)}
          onDone={roster.refetch}
          onClose={() => setChatterWeg(null)}
        >
          <ChatterWegUitleg chatter={chatterWeg}
                            subs={(roster.data ?? []).filter((s) => s.lead_chatter_id === chatterWeg.id)} />
        </VerwijderDialog>
      ) : null}

      {/* 2-11-2026 (Jordy): dezelfde dialoog als "Message or block", met reden verplicht. */}
      {headBlok ? (
        <BlokkeerDialoog
          account={headBlok}
          redenVerplicht
          onClose={() => setHeadBlok(null)}
          onGeblokkeerd={() => { setHeadBlok(null); roster.refetch() }}
        />
      ) : null}

      {editing ? (
        <ChatterEditDialog
          isOwner={isOwner}
          isAdmin={String(mij.data?.role ?? '').toLowerCase() === 'admin'}
          chatter={editing}
          personas={toewijsbaar.data ?? []}
          heads={(roster.data ?? []).filter((c) => c.is_lead && c.active)}
          onClose={() => setEditing(null)}
          onSaved={roster.refetch}
        />
      ) : null}
    </>
  )
}


/** Wat er bij het verwijderen van een chatter gebeurt, met wat er voor DEZE
 *  chatter speelt: sub-chatters die hun head kwijtraken, een login die
 *  meegaat, en een open saldo dat eerst uitbetaald kan worden. */
function ChatterWegUitleg({ chatter, subs }: { chatter: Chatter; subs: Chatter[] }) {
  const saldi = useApi<{ per_chatter: { chatter_id: number; open_commission: number | null }[] }>(
    '/api/admin/chatter-finance/sales-per-chatter-balances')
  const open = saldi.data?.per_chatter.find((r) => r.chatter_id === chatter.id)?.open_commission ?? 0
  return (
    <div style={{ fontSize: 13 }}>
      <p style={{ marginTop: 0 }}>
        <b>{chatter.name}</b> disappears from the roster, the shift agenda and every picker.
      </p>
      <ul style={{ fontSize: 12.5, margin: '0 0 10px 18px', padding: 0 }}>
        <li>Kept: payouts, sale assignments, manual earnings, worked shifts, chat history, audit log.</li>
        <li>Removed: assigned models, monitor rights, future shifts{chatter.has_login ? ', and the login (it can no longer sign in)' : ''}.</li>
      </ul>
      {subs.length ? (
        <div className="mut2" style={{ color: '#fcd34d', marginBottom: 8 }}>
          {subs.length === 1 ? '1 chatter reports' : `${subs.length} chatters report`} to {chatter.name}:{' '}
          {subs.map((s) => s.name).join(', ')}. They will have no head chatter afterwards.
        </div>
      ) : null}
      {saldi.loading ? (
        <div className="mut2" style={{ marginBottom: 8 }}>Checking open balance…</div>
      ) : open > 0 ? (
        <div className="mut2" style={{ color: '#fca5a5', marginBottom: 8 }}>
          Open balance: {eur(open)}. Consider recording the payout first — after deletion
          this chatter is no longer in the roster.
        </div>
      ) : null}
    </div>
  )
}
